'use client';

import Image from 'next/image';
import { type FC, useMemo } from 'react';
import { PaymentDetailsTable } from '@/components/PaymentDetails/PaymentDetails';
import { useCollapseList } from '@/hooks/useCollapseList.hook';
import type {
  FeaturedPaymentsImages,
  PaymentsAndWithdrawlsImages,
} from '@/types/bookmaker';
import MarkdownContent from '../MarkdownContent';
import {
  type PaymentOption,
  paymentOptionSchema,
} from '../PaymentDetails/schemas';
import { SectionImagesList } from './SectionImagesList';
import { HubSectionTitle } from './SectionTitle';

interface PaymentsSectionProps {
  title: string;
  text: string;
  images: PaymentsAndWithdrawlsImages;
  paymentOptions: unknown[] | null;
  featuredPayments: FeaturedPaymentsImages;
}

export const PaymentsSection: FC<PaymentsSectionProps> = ({
  title,
  text,
  images,
  paymentOptions,
  featuredPayments,
}) => {
  const {
    list: payments,
    handleToggle: handleFeaturedPaymentsToggle,
    haveHiddenItems: haveHiddenFeaturedPayments,
    isExpanded: isFeaturedPaymentsExpanded,
  } = useCollapseList({
    list: featuredPayments?.data ?? [],
    limit: 6,
  });
  const validatedPaymentOptions: PaymentOption[] = useMemo(() => {
    if (!paymentOptions) {
      return [];
    }

    return paymentOptions
      .map((paymentOption) => {
        const result = paymentOptionSchema.safeParse(paymentOption);
        if (!result.success) {
          console.warn('Invalid payment option:', paymentOption);
          return null;
        }
        return result.data;
      })
      .filter((item) => item !== null);
  }, [paymentOptions]);

  if (validatedPaymentOptions.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-lg bg-medium-gray p-4"
      id="payments"
    >
      <HubSectionTitle>{title}</HubSectionTitle>
      {images?.data && (
        <SectionImagesList
          imageAltPrefix="bookmaker-logo-payments-section"
          images={images.data}
        />
      )}
      <MarkdownContent>{text}</MarkdownContent>
      <div className="mb-7 flex flex-col gap-3">
        <p className="mb-3 font-bold text-2xl text-white">Payment Details</p>
        {validatedPaymentOptions.map((paymentOption) => {
          return (
            <PaymentDetailsTable
              key={paymentOption.name}
              paymentOption={paymentOption}
            />
          );
        })}
      </div>
      <div className="flex flex-col">
        <p className="mb-3 font-bold text-2xl text-white">Available payments</p>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {payments?.map((image) => (
            <div
              className="flex flex-row items-center gap-3 p-2"
              key={image.id}
            >
              <div className="h-[32px] w-[32px] object-scale-down">
                <Image
                  alt="bookmaker-logo-payments-section-3"
                  className="h-full rounded-lg"
                  height={0}
                  src={image?.attributes?.url}
                  width={32}
                />
              </div>
              <span className="font-normal">{image.attributes.caption}</span>
            </div>
          ))}
        </div>
        {haveHiddenFeaturedPayments && (
          <button
            aria-expanded={payments.length > 8}
            aria-label="Show more available payment methods"
            className="mt-7 flex justify-center rounded-lg border border-blue-gray border-solid px-[3.063rem] py-3 max-md:flex-1"
            onClick={() => handleFeaturedPaymentsToggle()}
            type="button"
          >
            <p
              className="mb-0 font-semibold text-blue-gray text-sm"
              style={{ marginBottom: '0px' }}
            >
              {isFeaturedPaymentsExpanded
                ? 'Show less available coins'
                : 'More available coins'}
            </p>
          </button>
        )}
      </div>
    </div>
  );
};
